import { create } from 'zustand';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useCustomerAgentAvailable } from '@/features/whatsapp/hooks/useCustomerAgentAvailable';

/* ==========================================================================
   Quem vê o Atendimento (change customer-agent-handoff, design D15)

   O painel decide pelas permissões do token. As claims `conversations.read`
   e `conversations.reply` só existem no próximo token (renovação, sem novo
   login), e a API sai antes do painel: um token velho com a tela aberta pode
   receber 403 das rotas de conversa mesmo com o painel achando que mostra.
   Esse 403 esconde a peça em silêncio, sem tela de erro, até a renovação do
   token — por isso a recusa é lembrada junto do token que a recebeu, e o
   token novo volta a mostrar tudo sem nenhum efeito de limpeza.
   ========================================================================== */

export const CONVERSATIONS_READ_PERMISSION = 'conversations.read';
export const CONVERSATIONS_REPLY_PERMISSION = 'conversations.reply';

interface ConversationsForbiddenState {
  /** O token que recebeu o 403; outro token volta a mostrar as peças. */
  forbiddenToken: string | null;
  markForbidden: (token: string) => void;
  clear: () => void;
}

export const useConversationsForbiddenStore = create<ConversationsForbiddenState>((set) => ({
  forbiddenToken: null,
  markForbidden: (token) => set({ forbiddenToken: token }),
  clear: () => set({ forbiddenToken: null }),
}));

/** Uma rota de conversa respondeu 403 com este token: as peças somem até ele ser renovado. */
export function markConversationsForbidden(): void {
  const token = useAuthStore.getState().accessToken;
  if (token) useConversationsForbiddenStore.getState().markForbidden(token);
}

/** O token corrente já recebeu 403 das rotas de conversa. */
export function useConversationsForbidden(): boolean {
  const token = useAuthStore((state) => state.accessToken);
  const forbiddenToken = useConversationsForbiddenStore((state) => state.forbiddenToken);
  return !!token && forbiddenToken === token;
}

export interface ConversationsAccess {
  /** Área disponível (flag, direito do plano, `conversations.read`, sem 404) e nenhum 403 com este token. */
  available: boolean;
  /** Flags ou direitos ainda carregando: não dá para dizer que não está disponível. */
  pending: boolean;
  /** O 403 de token antigo escondeu as peças. */
  forbidden: boolean;
}

/** O Atendimento aparece para este usuário? */
export function useConversationsAccess(): ConversationsAccess {
  const { available, pending } = useCustomerAgentAvailable(CONVERSATIONS_READ_PERMISSION);
  const forbidden = useConversationsForbidden();
  return { available: available && !forbidden, pending, forbidden };
}

/** O usuário pode agir sobre a conversa (assumir, responder, devolver, "só a equipe", "não atender"). */
export function useCanReplyConversations(): boolean {
  return useAuthStore((state) => state.user?.permissions.includes(CONVERSATIONS_REPLY_PERMISSION) ?? false);
}
